import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSatisfinderAuth } from '@/contexts/SatisfinderAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SatisfinderVerifyOTP() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useSatisfinderAuth();
  const email = params.get('email') || '';
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (!resendCooldown) return;
    const t = setInterval(() => setResendCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/satisfinder/verify-email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Verification failed'); return; }
      toast.success('Email verified. Welcome!');
      await refresh();
      nav('/satisfinder/dashboard');
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    await fetch(`${API}/satisfinder/resend-otp`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    toast.success('Code re-sent.');
    setResendCooldown(30);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm" data-testid="sf-verify-form">
        <SFBrand />
        <h1 className="font-['Montserrat'] text-2xl font-bold mt-6">Verify your email</h1>
        <p className="text-sm text-[#475569] mt-2">
          We sent a verification code to <span className="font-semibold">{email}</span>. Enter the code below to activate your satisFINDER® account.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold text-[#334155]">Verification code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s+/g, ''))}
              placeholder="Enter 6-digit code"
              className="mt-1 w-full px-3 py-3 border border-[#CBD5E1] rounded-lg text-lg tracking-widest text-center font-semibold focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]"
              inputMode="numeric"
              autoFocus
              data-testid="sf-otp-input"
            />
          </label>
          <button type="submit" disabled={submitting || !code} className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-3 rounded-lg text-sm" data-testid="sf-otp-submit">
            {submitting ? 'Verifying…' : 'Verify and Continue'}
          </button>
        </form>
        <button
          type="button" onClick={resend} disabled={resendCooldown > 0}
          className="w-full mt-3 text-sm text-[#1A3E6F] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="sf-otp-resend"
        >
          {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
        </button>
      </div>
    </div>
  );
}

function SFBrand() {
  return (
    <Link to="/satisfinder" className="flex items-center gap-2">
      <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 36, width: 'auto' }} />
    </Link>
  );
}
